import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Stethoscope, Loader2, Mail, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { t } from '../i18n';

export default function DoctorLogin() {
  const nav = useNavigate();
  const lang = localStorage.getItem('hb_lang') || 'en';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/doctor/auth/login', { email, password });
      localStorage.setItem('hb_token', data.token);
      localStorage.setItem('hb_doctor', JSON.stringify(data.doctor));
      toast.success(`Welcome, ${data.doctor.name}`);
      nav('/doctor/dashboard');
    } catch (e) {
      toast.error(t(lang, 'invalid_credentials'));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16 fade-up">
      <Card className="p-8 rounded-3xl border border-[#E8E1D5] bg-white hb-shadow-lg">
        <div className="text-center">
          <div className="h-14 w-14 rounded-2xl bg-[#1E3F33] grid place-items-center mx-auto"><Stethoscope className="h-7 w-7 text-white"/></div>
          <h1 className="font-display text-3xl text-[#1A1A1A] mt-4" data-testid="doctor-login-title">{t(lang,'doctor_portal')}</h1>
          <p className="text-[#4A4A4A] text-sm mt-2">{t(lang,'doctor_portal_sub')}</p>
        </div>

        {/* Portal Switcher Toggle */}
        <div className="flex rounded-full bg-[#F4F1EA] p-1 mt-6 border border-[#E8E1D5]">
          <button
            type="button"
            onClick={() => nav('/login')}
            className="flex-1 py-2 text-xs font-semibold rounded-full text-[#7A7A7A] hover:text-[#1E3F33] transition-all"
          >
            Patient Access
          </button>
          <button
            type="button"
            className="flex-1 py-2 text-xs font-semibold rounded-full transition-all bg-[#1E3F33] text-white shadow-sm"
          >
            Doctor Access
          </button>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <Label className="font-semibold text-[#1E3F33]">{t(lang,'email')}</Label>
            <div className="relative mt-1.5">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A7A7A]"/>
              <Input value={email} onChange={(e)=>setEmail(e.target.value)} type="email" required
                className="h-12 pl-10 rounded-xl border-[#E2E8F0]" data-testid="doctor-login-email"/>
            </div>
          </div>
          <div>
            <Label className="font-semibold text-[#1E3F33]">{t(lang,'password')}</Label>
            <div className="relative mt-1.5">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A7A7A]"/>
              <Input value={password} onChange={(e)=>setPassword(e.target.value)} type="password" required
                className="h-12 pl-10 rounded-xl border-[#E2E8F0]" data-testid="doctor-login-password"/>
            </div>
          </div>
          <Button type="submit" disabled={loading}
            className="w-full h-12 rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white font-semibold"
            data-testid="doctor-login-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2"/> : null} {t(lang,'login')}
          </Button>
        </form>
        <div className="mt-4 text-center text-sm text-[#7A7A7A]">
          {t(lang,'new_user')} <Link to="/doctor/register" className="text-[#1E3F33] font-semibold">{t(lang,'enroll_now')}</Link>
        </div>
        <div className="mt-2 text-center text-sm text-[#7A7A7A]">
          <Link to="/" className="hover:text-[#1E3F33]">← {t(lang,'back_home')}</Link>
        </div>
      </Card>
    </div>
  );
}